import type {QueryClient} from '@tanstack/react-query'

import type {MemexItemModel} from '../../../../models/memex-item-model'
import {isQueryPageOfItems} from '../../queries/query-data-helpers'
import type {MemexPageQueryData} from '../../queries/types'
import {getPaginatedQueryKeyWithVariables, getSubIssuesQueryKeyWithVariables} from '../helpers'

/**
 * Looks up a MemexItemModel by its id. If no item is found, undefined is returned.
 * If item exists in multiple pages of data (e.g., in multiple groups), the first occurrence is returned.
 * @param queryClient
 * @param id MemexItem id
 * @returns A MemexItemModel, or undefined if the item with this id does not exist
 */
export function findMemexItemByIdInQueryClient(queryClient: QueryClient, id: number) {
  return getMemexItemModelsFromQueryClient(queryClient).find((item: MemexItemModel) => item.id === id)
}

/**
 * Looks up a MemexItemModel by its id and returns all copies of the item in the current view.
 * @param queryClient
 * @param id MemexItem id
 * @returns An array of MemexItemModel
 */
export function findAllCopiesOfItemInQueryClient(queryClient: QueryClient, id: number) {
  return getMemexItemModelsFromQueryClient(queryClient).filter((item: MemexItemModel) => item.id === id)
}

/**
 * Looks up the index of a MemexItemModel by its id. If no item is found, -1 is returned.
 * If there are multiple pages of data, then the index will be the global index across all pages,
 * not within a single page of data.
 * If the item appears multiple times (e.g., in multiple groups), then the index of the first occurrence is returned.
 * @param queryClient
 * @param id MemexItem id
 * @returns The index of the item with provided id, or -1 if it does not exist
 */
export function findMemexItemGlobalIndexByIdInQueryClient(queryClient: QueryClient, id: number) {
  return getMemexItemModelsFromQueryClient(queryClient).findIndex((item: MemexItemModel) => item.id === id)
}

/**
 * A thin wrapper around `getQueryData`, that returns the current list of items from the QueryClient.
 * Helpful when we want to access the current state in a callback, but not trigger a re-render, for example
 * if we need to rollback in an error case after an optimistic update.
 * @param queryClient
 * @returns List of MemexItemModels representing the current state
 *
 * Note: creates a new array instance each time it is called, even if the items haven't changed.
 * Use `useMemexItems` hook to get a referentially stable array that only changes when a page of
 * items changes.
 */
export function getMemexItemModelsFromQueryClient(queryClient: QueryClient) {
  const baseKey = getPaginatedQueryKeyWithVariables(queryClient)
  const subIssuesKey = getSubIssuesQueryKeyWithVariables(queryClient)
  const queriesData = queryClient.getQueriesData<MemexPageQueryData>({queryKey: baseKey})
  const subIssuesQueriesData = queryClient.getQueriesData<MemexPageQueryData>({queryKey: subIssuesKey})
  // The result of `getQueriesData` is an array of arrays, where the first element is the query key,
  // and the second element is the query data. We want to flatten this into a single array of query data.
  return [...queriesData, ...subIssuesQueriesData].flatMap(([_queryKey, data]) =>
    isQueryPageOfItems(data) ? data.nodes : [],
  )
}
